/**
 * Portal-agnostic rewarded video ads (Y8, YouTube Playables, CrazyGames, Poki, GameDistribution, FRVR, dev grant).
 */

import { CrazyGamesBridge } from "./crazygames-bridge.js";
import { Y8Bridge } from "./y8-bridge.js";
import { YtBridge } from "./yt-bridge.js";
import { PokiBridge } from "./poki-bridge.js";
import { GdBridge } from "./gd-bridge.js";
import { FrvrBridge } from "./frvr-bridge.js";

function isDevBuild() {
  return typeof import.meta !== "undefined" && import.meta.env && import.meta.env.DEV;
}

/**
 * @param {unknown} result
 * @returns {{ granted: boolean, cancelled: boolean }}
 */
export function normalizeRewardedAdResult(result) {
  if (result && typeof result === "object") {
    return {
      granted: result.granted === true,
      cancelled: result.cancelled === true,
    };
  }
  return { granted: result === true, cancelled: false };
}

/** @param {unknown} result */
export function isRewardedAdGranted(result) {
  return normalizeRewardedAdResult(result).granted;
}

/** @param {unknown} result */
export function isRewardedAdCancelled(result) {
  return normalizeRewardedAdResult(result).cancelled;
}

/** @returns {boolean} */
export function canOfferRewardedAds() {
  if (PokiBridge.isEnabled()
    && typeof PokiBridge.canOfferRewardedAds === "function"
    && PokiBridge.canOfferRewardedAds()) {
    return true;
  }
  if (Y8Bridge.isEnabled()) return true;
  if (YtBridge.isEnabled()
    && typeof YtBridge.canOfferRewardedAds === "function"
    && YtBridge.canOfferRewardedAds()) {
    return true;
  }
  if (CrazyGamesBridge.isEnabled()
    && typeof CrazyGamesBridge.canOfferRewardedAds === "function"
    && CrazyGamesBridge.canOfferRewardedAds()) {
    return true;
  }
  if (GdBridge.isEnabled()
    && typeof GdBridge.canOfferRewardedAds === "function"
    && GdBridge.canOfferRewardedAds()) {
    return true;
  }
  if (FrvrBridge.isEnabled()
    && typeof FrvrBridge.canOfferRewardedAds === "function"
    && FrvrBridge.canOfferRewardedAds()) {
    return true;
  }
  return !!isDevBuild();
}

/**
 * @param {{
 *   name?: string,
 *   onStarted?: () => void,
 *   onFinished?: () => void,
 *   onError?: (err: unknown) => void
 * }} [options]
 * @returns {Promise<{ granted: boolean, cancelled: boolean }>}
 */
export function requestRewardedAd(options) {
  var opts = options || {};
  var pending;
  if (PokiBridge.isEnabled() && typeof PokiBridge.requestRewardedAd === "function") {
    pending = PokiBridge.requestRewardedAd(opts);
  } else if (Y8Bridge.isEnabled()) pending = Y8Bridge.requestRewardedAd(opts);
  else if (YtBridge.isEnabled()) pending = YtBridge.requestRewardedAd(opts);
  else if (CrazyGamesBridge.isEnabled()
    && typeof CrazyGamesBridge.requestRewardedAd === "function"
    && typeof CrazyGamesBridge.canOfferRewardedAds === "function"
    && CrazyGamesBridge.canOfferRewardedAds()) {
    pending = CrazyGamesBridge.requestRewardedAd(opts);
  } else if (GdBridge.isEnabled() && typeof GdBridge.requestRewardedAd === "function") {
    pending = GdBridge.requestRewardedAd(opts);
  } else if (FrvrBridge.isEnabled() && typeof FrvrBridge.requestRewardedAd === "function") {
    pending = FrvrBridge.requestRewardedAd(opts);
  } else if (isDevBuild()) {
    if (opts.onStarted) opts.onStarted();
    if (opts.onFinished) opts.onFinished();
    pending = Promise.resolve({ granted: true, cancelled: false });
  } else {
    if (opts.onError) opts.onError(new Error("rewarded ad unavailable"));
    pending = Promise.resolve({ granted: false, cancelled: false });
  }
  return Promise.resolve(pending).then(normalizeRewardedAdResult);
}
